import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from '@emotion/styled';
import { Box, Typography, IconButton, Chip, Button, Divider } from '@mui/material';
import { 
  X, MapPin, Mail, Phone, Building, Calendar, Tag, Globe, 
  Instagram, Facebook, Twitter, Link as LinkIcon, Edit2,
  Users, Award, Briefcase, Clock, CheckCircle2, XCircle,
  AlertCircle, ExternalLink
} from 'lucide-react';

const ModalOverlay = styled(motion.div)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  backdropFilter: 'blur(8px)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
});

const ModalContent = styled(motion.div)({
  background: 'white',
  borderRadius: '24px',
  maxWidth: '800px',
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 24px 48px -12px rgba(0, 0, 0, 0.18)',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '80vh',
});

const InfoRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '16px',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'rgba(0, 0, 0, 0.02)',
    transform: 'translateX(8px)',
  },
});

const SocialLink = styled(Button)({
  padding: '8px 16px',
  borderRadius: '12px',
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
    transform: 'translateY(-2px)',
  },
});

const StatusBadge = ({ status }) => {
  const getStatusConfig = (status) => {
    switch(status.toLowerCase()) {
      case 'active':
        return { 
          icon: CheckCircle2,
          bg: '#dcfce7', 
          color: '#16a34a',
          label: 'Active'
        };
      case 'decline':
        return { 
          icon: XCircle,
          bg: '#fee2e2', 
          color: '#dc2626',
          label: 'Declined'
        };
      default:
        return { 
          icon: AlertCircle,
          bg: '#fef3c7', 
          color: '#d97706',
          label: 'Pending'
        };
    }
  };

  const config = getStatusConfig(status);
  const StatusIcon = config.icon;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 0.5,
        borderRadius: '20px',
        bgcolor: config.bg,
        color: config.color,
        fontSize: '0.875rem',
        fontWeight: 600,
      }}
    >
      <StatusIcon size={16} />
      {config.label}
    </Box>
  );
};

const InfoCard = ({ icon: Icon, title, value, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
  >
    <Box
      sx={{
        p: 2,
        bgcolor: '#f8f9fa',
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          bgcolor: '#fff',
        }
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '12px',
          bgcolor: '#FF6C6C15',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#FF6C6C',
        }}
      >
        <Icon size={20} />
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  </motion.div>
);

const SocialButton = ({ icon: Icon, label, href }) => (
  <Button
    variant="outlined"
    size="small"
    startIcon={<Icon size={14} />}
    endIcon={<ExternalLink size={12} />}
    href={href}
    target="_blank"
    sx={{
      borderRadius: '8px',
      textTransform: 'none',
      borderColor: '#e5e7eb',
      color: 'text.primary',
      '&:hover': {
        borderColor: '#FF6C6C',
        bgcolor: '#FF6C6C10',
        transform: 'translateY(-2px)',
      },
      transition: 'all 0.2s ease',
    }}
  >
    {label}
  </Button>
);

const ViewBrandModal = ({ isOpen, onClose, brand }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
          >
            {/* Header - Fixed at top */}
            <Box sx={{ 
              p: 2,
              borderBottom: '1px solid #eee',
              bgcolor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 1
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Brand Details
                  </Typography>
                </motion.div>
                <IconButton onClick={onClose}>
                  <X size={20} />
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 260, damping: 20 }}
                >
                  <Box
                    component="img"
                    src={brand.logo || 'https://via.placeholder.com/150'}
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: '16px',
                      objectFit: 'cover',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    }}
                  />
                </motion.div>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {brand.brandName}
                    </Typography>
                    <StatusBadge status={brand.status} />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tag size={14} />
                    {brand.category}
                    <Box component="span" sx={{ mx: 1 }}>•</Box>
                    <MapPin size={14} />
                    {brand.location}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Scrollable Content */}
            <Box sx={{ 
              overflowY: 'auto',
              flex: 1,
              p: 2,
            }}>
              {/* Quick Stats */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: 2,
                mb: 2
              }}>
                <InfoCard 
                  icon={Mail} 
                  title="Email" 
                  value={brand.email}
                  delay={0.1}
                />
                <InfoCard 
                  icon={Phone} 
                  title="Phone" 
                  value={brand.phone}
                  delay={0.2}
                />
                <InfoCard 
                  icon={Calendar} 
                  title="Joined" 
                  value={brand.dateCreated}
                  delay={0.3}
                />
              </Box>

              {/* Social Links */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <SocialButton icon={Globe} label="Website" href="#" />
                <SocialButton icon={Instagram} label="Instagram" href="#" />
                <SocialButton icon={Facebook} label="Facebook" href="#" />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Details Grid */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: 2
              }}>
                {/* Business Details */}
                <Box>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: '#FF6C6C', 
                      mb: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Briefcase size={16} />
                    Business Details
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <InfoCard 
                      icon={Building} 
                      title="Year Established" 
                      value="2018"
                      delay={0.4}
                    />
                    <InfoCard 
                      icon={Users} 
                      title="Employee Count" 
                      value="50-100"
                      delay={0.5}
                    />
                    <InfoCard 
                      icon={Award} 
                      title="Business Type" 
                      value={brand.category}
                      delay={0.6}
                    />
                  </Box>
                </Box>

                {/* Location Details */}
                <Box>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: '#FF6C6C', 
                      mb: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <MapPin size={16} />
                    Location Details
                  </Typography>
                  <Box sx={{ bgcolor: '#f8f9fa', p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Branches
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      {['Accra', 'Kumasi', 'Takoradi'].map((branch, index) => (
                        <motion.div
                          key={branch}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.7 + (index * 0.1) }}
                        >
                          <Chip
                            label={branch}
                            size="small"
                            sx={{
                              bgcolor: 'white',
                              '&:hover': {
                                bgcolor: '#FF6C6C15',
                              }
                            }}
                          />
                        </motion.div>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Footer - Fixed at bottom */}
            <Box sx={{ 
              borderTop: '1px solid #eee',
              p: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              bgcolor: 'white',
              position: 'sticky',
              bottom: 0,
              zIndex: 1
            }}>
              <Button
                variant="outlined"
                size="small"
                onClick={onClose}
                sx={{ 
                  borderRadius: '8px',
                  borderColor: '#e5e7eb',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: '#d1d5db',
                    bgcolor: '#f9fafb'
                  }
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<Edit2 size={14} />}
                sx={{
                  borderRadius: '8px',
                  bgcolor: '#FF6C6C',
                  '&:hover': { bgcolor: '#ff8f8f' }
                }}
              >
                Edit Brand
              </Button>
            </Box>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default ViewBrandModal;
